import { Building, LineChart, Rocket, UserCog, DollarSign, PieChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function ConsultancyContent() {
    return (
        <div className="space-y-12">
            {/* Overview section */}
            <section>
                <h2 className="text-2xl font-semibold mb-6">Our Consultancy Services</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-3xl">
                    We provide tailored consulting solutions for businesses at every stage,
                    from fast-growing startups to established SMBs looking to optimize their
                    operations and drive growth.
                </p>

                <div className="grid md:grid-cols-2 gap-8">
                    {/* Fast Growing Startups Card */}
                    <Card className="border-2">
                        <CardHeader>
                            <Rocket className="h-8 w-8 mb-4 text-primary" />
                            <CardTitle>Fast Growing Startups</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground mb-4">
                                Comprehensive solutions for design, development, launch, product management,
                                growth, and operations.
                            </p>
                            <ul className="space-y-2 list-disc pl-5">
                                <li>Product strategy and development</li>
                                <li>Growth optimization</li>
                                <li>Operations scaling</li>
                                <li>Go-to-market planning</li>
                                <li>Tech stack optimization</li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Data-Driven SMB Card */}
                    <Card className="border-2">
                        <CardHeader>
                            <Building className="h-8 w-8 mb-4 text-primary" />
                            <CardTitle>Data-Driven SMB</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-muted-foreground mb-4">
                                Management consulting for local and SMB businesses focused on operational excellence.
                            </p>
                            <ul className="space-y-2 list-disc pl-5">
                                <li>Process frameworks & digital tooling</li>
                                <li>Revenue optimization</li>
                                <li>Operational efficiency</li>
                                <li>Progress assessment</li>
                                <li>Strategic pricing</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Process section */}
            <section>
                <h2 className="text-2xl font-semibold mb-6">Our Approach</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-muted rounded-lg p-6">
                        <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <LineChart className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-medium mb-2">Assessment</h3>
                        <p className="text-muted-foreground">
                            We perform a thorough analysis of your current processes, challenges, and opportunities.
                        </p>
                    </div>

                    <div className="bg-muted rounded-lg p-6">
                        <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <UserCog className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-medium mb-2">Implementation</h3>
                        <p className="text-muted-foreground">
                            Our experts work alongside your team to implement tailored solutions and frameworks.
                        </p>
                    </div>

                    <div className="bg-muted rounded-lg p-6">
                        <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                            <PieChart className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-medium mb-2">Optimization</h3>
                        <p className="text-muted-foreground">
                            Continuous monitoring and refinement to ensure optimal performance and results.
                        </p>
                    </div>
                </div>
            </section>

            {/* Client showcase */}
            <section>
                <h2 className="text-2xl font-semibold mb-6">Success Stories</h2>
                <div className="bg-muted p-8 rounded-xl">
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center">
                                <span className="text-lg font-semibold">BOILERPLATE</span>
                            </div>
                            <h3 className="font-semibold mb-2">VP Product & Growth</h3>
                            <p className="text-sm text-muted-foreground">
                                Revamped product strategy and accelerated growth by 150%.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center">
                                <span className="text-lg font-semibold">MAGIC</span>
                            </div>
                            <h3 className="font-semibold mb-2">Head of AI & COO</h3>
                            <p className="text-sm text-muted-foreground">
                                Implemented AI-driven solutions that reduced operational costs by 35%.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-24 h-24 bg-white rounded-full mx-auto mb-4 flex items-center justify-center">
                                <span className="text-lg font-semibold">GRADIENT</span>
                            </div>
                            <h3 className="font-semibold mb-2">Research Partner</h3>
                            <p className="text-sm text-muted-foreground">
                                Developed advanced research methodologies for healthcare data analysis.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
} 